/*
 * DoneGate — the terminal-free done-gate (design §9 SEAL-LOOP / §10 exchange).
 * It runs the SEAL-LOOP cockpit route and VISUALIZES (1) the step-9 four-plane
 * OPEN→ACK→CLOSE hop and (2) the four verifier gates turning green. Honest +
 * fail-closed: a red gate is painted RED with its name (the SPA never paints a
 * red gate green); the trigger is disabled-with-explanation behind the mTLS
 * boundary (the browser holds no client cert — same posture as Cockpit's Deploy
 * gate). Reuses only existing Sovereign-Night components/tokens; no new token.
 *
 * Structure deliberately parallels Cockpit.tsx's DeployAndExchange so a reviewer
 * sees the same honesty discipline: the run is reached only behind the proxy,
 * runSealLoop() is wired (with setError on rejection) so the affordance is real,
 * and the failed_gate callout is LOUD --red and names the gate — the visual proof
 * the loop is fail-closed.
 */

import { useState } from "react";
import { runSealLoop, type SealLoopRouteResult } from "../api/cockpit";
import { PlaneState } from "../components/PlaneState";
import { SpecRef } from "../components/SpecRef";
import { VerifyBadge } from "../components/VerifyBadge";
import { Hash } from "../components/Hash";
import type { Role, VerifyState } from "../api/types";

/* The five verifier gates, in seal order (cli/mesh_cli/sealloop/commands.py default_gates). */
const GATES = [
  "exchange",
  "trust-log-verify",
  "audit-verify",
  "trust-witnesses-verify",
  "spec-verify",
] as const;

/* The audit-event hops of one step-9 exchange (test_somalia_intramesh_step9.py: open→ack→close). */
const HOPS = ["OPEN", "ACK", "CLOSE"] as const;

/**
 * The honest per-gate verdict, fail-closed by construction:
 *   - no result yet                  → "unknown"  (dormant — never green)
 *   - gate never attempted (post-red)→ "pending"  (honest, NOT green-by-omission)
 *   - gate attempted & sealed        → "verified" (teal)
 *   - gate attempted & not sealed    → "failed"   (LOUD red — never faked green)
 * The SPA never paints a red (or absent) gate green.
 */
function gateVerdict(r: SealLoopRouteResult | null, name: string): VerifyState {
  if (!r) return "unknown";
  const g = r.gate_results.find((x) => x.name === name);
  if (!g) return "pending"; // gate after the first red is OMITTED — honest, not green
  return g.sealed ? "verified" : "failed"; // red gate → loud failed, never faked green
}

export default function DoneGate({ role }: { role: Role }) {
  const [result, setResult] = useState<SealLoopRouteResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const isOperator = role === "operator";

  // The browser can never present the mTLS client cert the Tier-1 route requires,
  // so the trigger is disabled-with-explanation; the run is reached behind the
  // proxy that forwards the verified X-Operator-Cert-Thp. The button is always
  // disabled here, but runSealLoop() is wired so the affordance is real (and any
  // rejection is surfaced honestly via setError) behind the proxy.
  const gateReason = !isOperator
    ? "Operator role required for the Tier-1 done-gate"
    : "Reached only behind the mTLS-terminating proxy — the browser holds no client cert";

  async function onRun() {
    setError(null);
    setResult(null);
    setRunning(true);
    try {
      const r = await runSealLoop();
      setResult(r);
    } catch (e) {
      // A real POST with no live wiring is 503 — surfaced as the thrown HTTP
      // error and rendered loudly, never swallowed into a fabricated success.
      setError(e instanceof Error ? e.message : "seal-loop failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <Header converged={result?.converged} />

      {/* The done-gate run affordance — disabled-with-explanation (mTLS boundary). */}
      <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
        <SectionHeading
          tier="Tier 1 · Done-gate"
          title="Run the SEAL-LOOP"
          note="The route self-audits its §38.3 open event BEFORE the run and aborts 503 without running if the seal fails (audit-first). It runs the step-9 exchange + the four verifier gates over the node's REAL deployed stores and returns the honest per-gate verdict — a still-red gate is reported faithfully (the loop is fail-closed). The panel never runs the loop itself; it cannot present a client cert, so the trigger is disabled-with-explanation."
        />

        <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
          <button
            type="button"
            disabled
            aria-disabled="true"
            onClick={() => void onRun()}
            title={`POST /v1/cockpit/seal-loop (Tier-1, mTLS operator cert) — ${gateReason}`}
            style={buttonStyle(true, "var(--teal)")}
          >
            {running ? "Running done-gate…" : "Run done-gate…"}
          </button>
          <VerifyBadge
            state={doneGateVerdict(result)}
            label={doneGateLabel(result)}
            size="sm"
          />
          <span
            className="mono"
            style={{ fontSize: "11.5px", color: "var(--fg-faint)", maxWidth: "60ch" }}
          >
            {gateReason}
          </span>
        </div>

        {error && (
          <PlaneState
            kind="error"
            title="Done-gate could not run"
            message={error}
            specRef="Design §9"
          />
        )}
      </div>

      <ExchangeStrip result={result} />
      <GateRow result={result} />
      <SealMetric result={result} />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Header                                                             */
/* ------------------------------------------------------------------ */

function Header({ converged }: { converged?: boolean }) {
  const stage =
    converged === undefined ? "not yet run" : converged ? "converged" : "not converged";
  const accent =
    converged === undefined ? "var(--fg-faint)" : converged ? "var(--teal)" : "var(--red)";
  return (
    <header className="view-header">
      <div className="view-header__eyebrow">
        <span className="accent-teal">Done-gate</span>
        <span><SpecRef text="Operator · §9" /></span>
      </div>
      <h1 className="view-header__title">The Done-gate</h1>
      <p className="view-header__sub">
        The terminal-free SEAL-LOOP window. One run closes the done-gate: the
        step-9 four-plane OPEN→ACK→CLOSE hop, then the four verifier gates turning
        green over the node's real deployed stores. Honest and fail-closed — a red
        gate is painted red with its name; the panel never fakes a green.{" "}
        <span className="mono" style={{ color: accent }}>
          loop: {stage}
        </span>
      </p>
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* The OPEN→ACK→CLOSE audit-event strip                               */
/* ------------------------------------------------------------------ */

function ExchangeStrip({ result }: { result: SealLoopRouteResult | null }) {
  // The exchange gate's verdict drives the strip: until the loop runs and the
  // exchange gate seals, the hops stay dormant (never asserted complete).
  const exchangeSealed = result
    ? (result.gate_results.find((g) => g.name === "exchange")?.sealed ?? false)
    : false;
  const ran = result !== null;

  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <SectionHeading
        tier="Step 9 · Exchange"
        title="OPEN → ACK → CLOSE"
        note="The three audit events the consent-gated four-plane exchange seals (open → ack → close). The strip lights up only once the exchange gate seals over the live audit chain — until then the hops are dormant, never asserted complete."
      />
      <ol
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
        }}
      >
        {HOPS.map((hop, i) => (
          <li
            key={hop}
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            <span
              className="panel panel--inset"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 14px",
                borderColor: exchangeSealed ? "var(--ember)" : "var(--hair)",
                opacity: exchangeSealed ? 1 : 0.6,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  background: exchangeSealed ? "var(--ember)" : "var(--fg-decor)",
                  flex: "0 0 auto",
                }}
              />
              <span className="mono" style={{ fontSize: "12px" }}>
                {i + 1}. {hop}
              </span>
            </span>
            {i < HOPS.length - 1 && (
              <span aria-hidden="true" className="mono" style={{ color: "var(--fg-faint)" }}>
                →
              </span>
            )}
          </li>
        ))}
      </ol>
      <span className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)" }}>
        {!ran
          ? "Dormant — run the done-gate to seal the open → ack → close events over the live audit chain."
          : exchangeSealed
            ? "✓ exchange gate sealed — the open → ack → close events verified over the live audit chain."
            : "⦸ exchange gate did not seal — the four-plane hop is not asserted complete (fail-closed)."}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The four verifier gates turning green                              */
/* ------------------------------------------------------------------ */

function GateRow({ result }: { result: SealLoopRouteResult | null }) {
  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <SectionHeading
        tier="Verifier gates · §9"
        title="The four gates turning green"
        note="Each verifier gate (after the exchange) reuses the existing audited verify_log / verify_audit_chain / verify_witnesses / verify_spec_freeze over the node's real deployed stores. A gate that did not seal is painted RED and named; a gate after the first red is omitted by the route and shown as pending — never green-by-omission. The SPA never paints a red gate green."
      />
      <ul
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {GATES.map((name) => {
          const verdict = gateVerdict(result, name);
          const g = result?.gate_results.find((x) => x.name === name) ?? null;
          return (
            <li
              key={name}
              className="panel panel--inset"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "12px 16px",
              }}
            >
              <span className="mono" style={{ fontSize: "13px" }}>
                {name}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
                {g && (
                  <span className="mono" style={{ fontSize: "11.5px", color: "var(--fg-faint)" }}>
                    rc {g.rc}
                  </span>
                )}
                <VerifyBadge state={verdict} size="sm" />
              </span>
            </li>
          );
        })}
      </ul>

      {/* The failed_gate callout — LOUD --red, naming the gate. The visual proof
          the loop is fail-closed: a red gate is never dressed up as a green. */}
      {result && !result.converged && result.failed_gate && (
        <div
          role="alert"
          className="panel panel--inset"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            borderColor: "var(--red)",
            background: "var(--red-dim)",
            padding: "12px 16px",
          }}
        >
          <span aria-hidden="true" style={{ color: "var(--red)", fontWeight: 700 }}>
            ✕
          </span>
          <span style={{ color: "var(--red)", fontWeight: 600 }}>
            Done-gate did not converge — first red gate:{" "}
            <span className="mono">{result.failed_gate}</span>. The loop is fail-closed; this
            gate is shown red, never faked green.
          </span>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* The sealed-progress metric + the audit-open hash                   */
/* ------------------------------------------------------------------ */

function SealMetric({ result }: { result: SealLoopRouteResult | null }) {
  return (
    <div className="panel" style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <SectionHeading
        tier="Metric · §9"
        title="Gate progress &amp; the live §52 AC count"
        note="Two SEPARATE counts, never folded into one fraction: gate progress (sealed gates over the loop's gate set) and the LIVE §52 AC-row count. Reading gate progress as an 'X of Y ACs sealed' fraction would be wrong — they measure different things."
      />
      {result === null ? (
        <span className="mono" style={{ fontSize: "12.5px", color: "var(--fg-faint)" }}>
          — pending (run the done-gate to read the gate-progress metric)
        </span>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}
          >
            <span className="mono" style={{ fontSize: "12.5px" }}>
              <span style={{ color: "var(--fg-faint)" }}>gate progress: </span>
              <span style={{ color: result.converged ? "var(--teal)" : "var(--fg)" }}>
                {result.metric.sealed_gates}/{result.metric.gate_count} sealed (
                {result.metric.gate_seal_pct}%)
              </span>
            </span>
            <span className="mono" style={{ fontSize: "12.5px" }}>
              <span style={{ color: "var(--fg-faint)" }}>live §52 ACs: </span>
              <span style={{ color: "var(--fg-muted)" }}>{result.metric.total_acs}</span>
            </span>
          </div>
          <Hash value={result.audit_open_hash} label="audit open" accent="var(--gold)" />
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small helpers                                                      */
/* ------------------------------------------------------------------ */

/** The overall done-gate verdict for the run badge — honest, never faked green. */
function doneGateVerdict(result: SealLoopRouteResult | null): VerifyState {
  if (!result) return "unknown"; // dormant — not yet run
  return result.converged ? "verified" : "failed"; // not-converged → loud red, never green
}

function doneGateLabel(result: SealLoopRouteResult | null): string {
  if (!result) return "Not yet run";
  return result.converged ? "Done-gate closed" : "Did not converge";
}

function SectionHeading({ tier, title, note }: { tier: string; title: string; note: string }) {
  return (
    <div>
      <div
        className="mono"
        style={{
          fontSize: "11px",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--indigo)",
          marginBottom: "4px",
        }}
      >
        {tier}
      </div>
      <h2 style={{ fontSize: "20px", margin: "0 0 6px" }}>{title}</h2>
      <p style={{ color: "var(--fg-faint)", margin: 0, fontSize: "13px", maxWidth: "82ch" }}>
        {note}
      </p>
    </div>
  );
}

function buttonStyle(disabled: boolean, accent: string): React.CSSProperties {
  return {
    minHeight: "44px",
    padding: "0 18px",
    borderRadius: "var(--radius-sm)",
    border: `1px solid ${disabled ? "var(--hair-strong)" : accent}`,
    background: disabled ? "var(--bg)" : "transparent",
    color: disabled ? "var(--fg-faint)" : accent,
    fontFamily: "var(--font-mono)",
    fontSize: "13px",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.7 : 1,
  };
}
